import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import knowledgeApi from '@/lib/knowledge-api'

export interface Document {
  id: number
  name: string
  size: number
  status: string
  progress: number
  token_count: number
  chunk_count: number
  created_at: string
  updated_at: string
  ragflow_document_id: string | null
  meta_fields?: Record<string, any>
}

export interface DocumentsResponse {
  documents: Document[]
  pagination: {
    total: number
    limit: number
    offset: number
    has_next: boolean
    has_previous: boolean
  }
}

export function useDocuments(kbId: string, limit: number = 10, offset: number = 0, keywords?: string) {
  return useQuery({
    queryKey: ['documents', kbId, limit, offset, keywords],
    queryFn: async () => {
      const params: Record<string, any> = { limit, offset }
      if (keywords) {
        params.keywords = keywords
      }
      const response = await knowledgeApi.get(`/knowledge/${kbId}/documents/status/`, {
        params,
      })
      return response.data as DocumentsResponse
    },
    enabled: !!kbId,
  })
}

export function useUploadDocuments(kbId: string) {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (files: File[]) => {
      const formData = new FormData()
      files.forEach((file) => {
        formData.append('files', file)
      })
      const response = await knowledgeApi.post(`/knowledge/${kbId}/upload/`, formData, {
        headers: { 
          // Don't set Content-Type - let axios set it with correct boundary
        },
      })
      return response.data
    },
    onSuccess: () => {
      // Invalidate documents list to show new uploads
      queryClient.invalidateQueries({ queryKey: ['documents', kbId] })
      // Invalidate KB stats to update document counts
      queryClient.invalidateQueries({ queryKey: ['knowledge-base', kbId] })
    },
  })
}

export function useParseDocuments(kbId: string) {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (documentIds: string[]) => {
      await knowledgeApi.post(`/knowledge/${kbId}/documents/parse/`, 
        { document_ids: documentIds }
      )
    },
    onSuccess: () => {
      // Invalidate documents list to show updated status
      queryClient.invalidateQueries({ queryKey: ['documents', kbId] })
      // Invalidate KB stats to update ready document count
      queryClient.invalidateQueries({ queryKey: ['knowledge-base', kbId] })
    },
  })
}

export function useDeleteDocuments(kbId: string) {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (documentIds: string[]) => {
      const response = await knowledgeApi.post(
        `/knowledge/${kbId}/documents/delete/`,
        { document_ids: documentIds }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', kbId] })
    },
  })
}

// Update document metadata
export function useUpdateDocumentMetadata(kbId: string) {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ 
      ragflowDocumentId, 
      metaFields 
    }: { 
      ragflowDocumentId: string
      metaFields: Record<string, any>
    }) => {
      const response = await knowledgeApi.put(
        `/knowledge/${kbId}/documents/${ragflowDocumentId}/metadata/`,
        { meta_fields: metaFields }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', kbId] })
    },
  })
}
